
import React from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { formatUssdTelUrl } from '@/utils/ussd-utils';

interface CallMeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CallMeDialog: React.FC<CallMeDialogProps> = ({ open, onOpenChange }) => {
  const { t } = useTranslation();

  const phoneFormSchema = z.object({
    phoneNumber: z.string()
      .min(8, t('tools.validation.phoneLength'))
      .regex(/^77\d{6}$/, t('tools.validation.phoneFormat'))
  });

  const form = useForm<z.infer<typeof phoneFormSchema>>({
    resolver: zodResolver(phoneFormSchema),
    defaultValues: {
      phoneNumber: ""
    }
  });

  const onSubmit = (data: z.infer<typeof phoneFormSchema>) => {
    window.location.href = formatUssdTelUrl(`*167#${data.phoneNumber}#`);
    onOpenChange(false);
    form.reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('tools.callme.dialogTitle')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input 
                      placeholder="77999999" 
                      {...field} 
                      type="tel"
                      pattern="[0-9]*"
                      inputMode="numeric" 
                      className="text-lg"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit">{t('tools.callme.send')}</Button>
            </DialogFooter>
          </form>
        </Form>
        <p className="text-sm text-muted-foreground mt-2">
          {t('tools.callme.hint')}
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default CallMeDialog;
